import * as THREE from 'three';
import { getPortalOutlinePoint } from './portalProfiles';
import { getPortalWarpProfile } from './portalWarpProfiles';

const TAU = Math.PI * 2;
const smooth = THREE.MathUtils.smoothstep;

export const getWarpOutline = (profile, angle) => {
  if (profile.contour === 'Armory') {
    const sector = Math.PI / 4;
    const local =
      ((((angle + sector / 2) % sector) + sector) % sector) - sector / 2;
    const radius = Math.cos(sector / 2) / Math.cos(local);
    return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
  }
  if (profile.contour === 'Shrine' || profile.contour === 'Warden') {
    const radius =
      profile.contour === 'Shrine'
        ? 0.88 + Math.cos(angle * 6) * 0.12
        : 0.9 + Math.cos(angle * 8) * 0.1 + Math.sin(angle * 13) * 0.035;
    return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
  }
  return getPortalOutlinePoint(profile.contour, angle);
};

const glow = (color, opacity = 1) =>
  new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false,
    toneMapped: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });

const makeTokenGeometry = (token) => {
  if (token === 'cross') {
    const shape = new THREE.Shape();
    [
      [-0.08, -0.3],
      [0.08, -0.3],
      [0.08, -0.08],
      [0.3, -0.08],
      [0.3, 0.08],
      [0.08, 0.08],
      [0.08, 0.3],
      [-0.08, 0.3],
      [-0.08, 0.08],
      [-0.3, 0.08],
      [-0.3, -0.08],
      [-0.08, -0.08],
    ].forEach(([x, y], index) =>
      index ? shape.lineTo(x, y) : shape.moveTo(x, y)
    );
    shape.closePath();
    return new THREE.ExtrudeGeometry(shape, {
      depth: 0.04,
      bevelEnabled: false,
    });
  }
  if (token === 'star') {
    const shape = new THREE.Shape();
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * TAU,
        radius = i % 2 ? 0.14 : 0.38;
      if (i) shape.lineTo(Math.cos(angle) * radius, Math.sin(angle) * radius);
      else shape.moveTo(Math.cos(angle) * radius, Math.sin(angle) * radius);
    }
    shape.closePath();
    return new THREE.ExtrudeGeometry(shape, {
      depth: 0.03,
      bevelEnabled: false,
    });
  }
  if (token === 'cargo' || token === 'scrap')
    return new THREE.BoxGeometry(0.4, 0.4, 0.4);
  if (token === 'mote') return new THREE.IcosahedronGeometry(0.11, 1);
  if (token === 'blade' || token === 'rift-blade')
    return new THREE.ConeGeometry(0.15, 0.8, 4);
  if (token === 'bolt') return new THREE.ConeGeometry(0.12, 0.65, 3);
  if (token === 'fracture' || token === 'core-shard')
    return new THREE.TetrahedronGeometry(0.28);
  return new THREE.OctahedronGeometry(0.28);
};

const membraneFragment = `
  varying vec2 vUv;
  uniform float time;
  uniform float power;
  uniform float mode;
  uniform vec3 tint;
  uniform vec3 accent;
  float band(float v, float width) { return exp(-pow(v / width, 2.0)); }
  float hash(vec2 p) { return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453); }
  void main() {
    float a = vUv.x * 6.283185;
    float z = vUv.y;
    float run = z * 34.0 - time * (2.0 + power * 5.0);
    float rib = pow(0.5 + 0.5 * sin(run * 3.0), 24.0);
    float silk = pow(0.5 + 0.5 * sin(a * 6.0 + run * 0.8), 12.0);
    float pattern = silk * 0.3 + rib * 0.2;
    float blend = 0.5 + 0.5 * sin(a + z * 14.0 - time);
    if (mode < 0.5) {
      // Interlaced double helix, with broad healing pulses.
      pattern = pow(0.5 + 0.5 * sin(a * 2.0 + run), 18.0) * 0.75;
      pattern += pow(0.5 + 0.5 * sin(run * 0.45), 12.0) * 0.18;
    } else if (mode < 1.5) {
      vec2 grid = fract(vec2(vUv.x * 6.0, z * 24.0 - time));
      pattern = band(grid.x - 0.5, 0.018) * 0.5 + band(grid.y - 0.5, 0.025) * 0.3;
      pattern += step(0.9, hash(floor(vec2(vUv.x * 24.0, z * 90.0)))) * rib * 0.65;
    } else if (mode < 2.5) {
      vec2 facet = fract(vec2(vUv.x * 9.0 + z * 13.0, z * 28.0 - time * 0.7));
      pattern = pow(abs(facet.x - facet.y), 9.0) * 0.8 + silk * 0.3;
      blend = hash(floor(vec2(vUv.x * 9.0 + z * 13.0, z * 28.0 - time * 0.7)));
    } else if (mode < 3.5) {
      float rail = pow(0.5 + 0.5 * cos(a * 4.0), 38.0);
      pattern = rail * 0.6 + rib * 0.12;
      pattern *= 0.65 + 0.35 * sin(a * 11.0 + z * 12.0 + time * 0.6);
    } else if (mode < 4.5) {
      pattern = pow(0.5 + 0.5 * sin(a * 3.0 + z * 18.0 - time * 0.4
        + sin(z * 10.0 + time) * 1.7), 6.0) * 0.25;
      pattern += band(sin(a * 4.0 + run * 0.18), 0.045) * 0.28;
    } else if (mode < 5.5) {
      float fire = sin(a * 4.0 + run * 1.3 + sin(run + a * 9.0));
      pattern = pow(0.5 + 0.5 * fire, 9.0) * 0.55 + rib * 0.15;
    } else if (mode < 6.5) {
      pattern = pow(0.5 + 0.5 * cos(a * 8.0), 70.0) * 0.8;
      pattern += rib * pow(0.5 + 0.5 * cos(a * 8.0 + 1.0), 18.0) * 0.5;
    } else if (mode < 7.5) {
      float arch = abs(sin(a * 3.0)) - 0.65 + sin(z * 24.0 - time) * 0.25;
      pattern = band(arch, 0.035) * 0.6 + silk * 0.15;
      pattern += step(0.97, hash(floor(vec2(a * 14.0, z * 120.0)))) * 0.5;
    } else if (mode < 8.5) {
      vec2 grid = fract(vec2(vUv.x * 4.0, z * 36.0 - time * 2.0));
      pattern = band(grid.x - 0.5, 0.018) * step(0.3, grid.y) * 0.6 + rib * 0.13;
    } else if (mode < 9.5) {
      float fold = a * 5.0 + run + sin(a * 7.0 - time) * 2.0;
      pattern = pow(0.5 + 0.5 * sin(fold), 12.0) * 0.7;
      pattern += band(sin(run * 0.3 + a * 2.0), 0.035) * 0.4;
      blend = 0.5 + 0.5 * sin(a * 3.0 - z * 10.0 + time * 0.8);
    } else if (mode < 10.5) {
      float storm = a * 9.0 + z * 44.0 - time * 1.8;
      pattern = pow(0.5 + 0.5 * sin(storm + sin(z * 20.0 - time)), 22.0) * 0.8;
      pattern += rib * pow(0.5 + 0.5 * cos(a * 6.0), 12.0) * 0.55;
      blend = 0.5 + 0.5 * sin(a * 2.0 + z * 15.0 - time * 0.6);
    } else {
      float cut = sin(a * 4.0 + floor(z * 32.0) * 1.7 + time * 0.6);
      pattern = band(cut, 0.08) * 0.65 + rib * 0.18;
    }
    vec3 color = mix(tint, accent, blend) * (0.025 + pattern * (0.7 + power * 0.5));
    float alpha = 0.12 + pattern * 0.7;
    gl_FragColor = vec4(color, alpha);
    #include <colorspace_fragment>
  }
`;

export const createPortalTunnel = (route = {}) => {
  const profile = getPortalWarpProfile(typeof route === 'string' ? {} : route);
  const core = profile.id === 'core';
  const warden = profile.id === 'warden';
  const group = new THREE.Group();
  group.name = `portal-warp-${profile.id}`;
  group.userData.profile = profile.id;
  const length = core ? 160 : 110;
  const primary = new THREE.Color(profile.primary),
    secondary = new THREE.Color(profile.secondary);
  const membrane = new THREE.ShaderMaterial({
    uniforms: {
      time: { value: 0 },
      power: { value: 0 },
      mode: { value: profile.mode },
      tint: { value: primary },
      accent: { value: secondary },
    },
    vertexShader: `varying vec2 vUv; uniform float time; uniform float power;
      void main() { vUv = uv; vec3 p = position;
        p.xz *= 1.0 + sin(uv.y * 24.0 - time * 0.7) * 0.025 * power;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p,1.0); }`,
    fragmentShader: membraneFragment,
    side: THREE.BackSide,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
  const wallGeometry = new THREE.CylinderGeometry(4, 4, length, 72, 48, true);
  const wallPositions = wallGeometry.attributes.position;
  for (let i = 0; i < wallPositions.count; i++) {
    const angle = Math.atan2(wallPositions.getZ(i), wallPositions.getX(i));
    const outline = getWarpOutline(profile, angle);
    wallPositions.setX(i, outline.x * 4);
    wallPositions.setZ(i, outline.y * 4);
  }
  wallGeometry.computeVertexNormals();
  const skin = new THREE.Mesh(wallGeometry, membrane);
  skin.rotation.x = Math.PI / 2;
  skin.position.set(0, 0.65, -length / 2);
  group.add(skin);

  const ringPoints = Array.from({ length: 96 }, (_, i) => {
    const point = getWarpOutline(profile, (i / 96) * TAU);
    return new THREE.Vector3(point.x * 3.7, point.y * 3.7, 0);
  });
  const broken = ['anomaly', 'warden'].includes(profile.id);
  const ringGeometry = new THREE.BufferGeometry().setFromPoints(
    broken
      ? ringPoints.flatMap((point, i) =>
          i % 9 < 5 ? [point, ringPoints[(i + 1) % 96]] : []
        )
      : ringPoints
  );
  const ringMaterials = [primary, secondary].map(
    (color) =>
      new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
        toneMapped: false,
        blending: THREE.AdditiveBlending,
      })
  );
  const rings = Array.from({ length: core ? 40 : 27 }, (_, i) => {
    const ring = broken
      ? new THREE.LineSegments(ringGeometry, ringMaterials[i % 2])
      : new THREE.LineLoop(ringGeometry, ringMaterials[i % 2]);
    ring.position.set(0, 0.65, -3 - i * 3.8);
    ring.rotation.z = i * profile.twist * 2;
    group.add(ring);
    return ring;
  });
  const ribbons = new THREE.Group();
  ribbons.position.y = 0.65;
  const ribbonCount = core ? 12 : profile.id === 'recovery' ? 4 : 8;
  for (let strand = 0; strand < ribbonCount; strand++) {
    const points = Array.from({ length: 128 }, (_, i) => {
      const z = (i / 127) * length;
      const angle = (strand / ribbonCount) * TAU + z * profile.twist;
      const point = getWarpOutline(profile, angle);
      return new THREE.Vector3(point.x * 3.45, point.y * 3.45, -z);
    });
    const ribbon = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(points),
      new THREE.LineBasicMaterial({
        color: strand % 2 ? primary : secondary,
        transparent: true,
        opacity: core ? 0.45 : 0.3,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        toneMapped: false,
      })
    );
    ribbons.add(ribbon);
  }
  group.add(ribbons);

  const tokenMaterial = core
    ? new THREE.MeshPhysicalMaterial({
        color: '#24152e',
        emissive: '#8c244a',
        emissiveIntensity: 0.35,
        metalness: 0.7,
        roughness: 0.28,
      })
    : glow('#ffffff', 0.7);
  if (profile.token === 'cargo' || profile.token === 'scrap')
    tokenMaterial.wireframe = true;
  const tokens = new THREE.InstancedMesh(
    makeTokenGeometry(profile.token),
    tokenMaterial,
    core ? 110 : warden ? 88 : 64
  );
  tokens.name = `warp-${profile.token}s`;
  tokens.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  tokens.frustumCulled = false;
  const dummy = new THREE.Object3D();
  for (let i = 0; i < tokens.count; i++)
    tokens.setColorAt(i, i % 3 ? primary : secondary);
  group.add(tokens);
  if (core) {
    const edges = new THREE.InstancedMesh(
      tokens.geometry,
      glow('#f3bbff', 0.6),
      tokens.count
    );
    edges.material.wireframe = true;
    edges.instanceMatrix = tokens.instanceMatrix;
    edges.instanceColor = tokens.instanceColor;
    edges.frustumCulled = false;
    group.add(edges);
  }
  const streakCount = core ? 180 : warden ? 140 : 100;
  const streakPositions = new Float32Array(streakCount * 6);
  const streakGeometry = new THREE.BufferGeometry();
  streakGeometry.setAttribute(
    'position',
    new THREE.BufferAttribute(streakPositions, 3).setUsage(
      THREE.DynamicDrawUsage
    )
  );
  const particles = new THREE.LineSegments(
    streakGeometry,
    new THREE.LineBasicMaterial({
      color: profile.secondary,
      transparent: true,
      opacity: 0.65,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    })
  );
  particles.frustumCulled = false;
  group.add(particles);

  const gates =
    core || warden
      ? (core ? [-24, -59, -94] : [-18, -42, -66]).map((z, index) => {
          const gate = new THREE.Group();
          gate.name = `${core ? 'core-containment' : 'warden-siege'}-gate-${
            index + 1
          }`;
          gate.position.set(0, 0.65, z);
          const outline = new THREE.Mesh(
            new THREE.TorusGeometry(3.8, 0.035, 6, 96),
            glow(profile.secondary, 0.8)
          );
          gate.add(outline);
          const shape = new THREE.Shape();
          shape.moveTo(0, 0.65);
          shape.lineTo(1.1, 3.4);
          shape.lineTo(-1.1, 3.4);
          shape.closePath();
          const geometry = new THREE.ExtrudeGeometry(shape, {
            depth: 0.12,
            bevelEnabled: false,
          });
          const bladeCount = core ? 8 : 4;
          const blades = Array.from({ length: bladeCount }, (_, i) => {
            const blade = new THREE.Mesh(
              geometry,
              new THREE.MeshBasicMaterial({
                color: core
                  ? i % 2
                    ? '#29103f'
                    : '#421a54'
                  : i % 2
                  ? '#592319'
                  : '#321a22',
                side: THREE.DoubleSide,
              })
            );
            const edge = new THREE.LineSegments(
              new THREE.EdgesGeometry(geometry),
              new THREE.LineBasicMaterial({
                color: i % 2 ? primary : secondary,
                transparent: true,
                opacity: 0.8,
                toneMapped: false,
              })
            );
            blade.add(edge);
            blade.rotation.z = (i / bladeCount) * TAU;
            gate.add(blade);
            return blade;
          });
          group.add(gate);
          return { gate, blades, z };
        })
      : [];

  let horizon = null;
  if (core) {
    horizon = new THREE.Mesh(
      new THREE.PlaneGeometry(50, 50),
      new THREE.ShaderMaterial({
        uniforms: { time: { value: 0 }, power: { value: 0 } },
        transparent: true,
        depthWrite: false,
        vertexShader: `varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
        fragmentShader: `varying vec2 vUv;uniform float time;uniform float power;
        void main(){vec2 p=(vUv-.5)*2.0;float r=length(p);float a=atan(p.y,p.x+.0001);
          float ring=exp(-pow((r-.28)*35.0,2.0));
          float storm=pow(.5+.5*sin(a*13.0-r*40.0+time),12.0)*exp(-abs(r-.35)*10.0);
          vec3 color=mix(vec3(.45,.12,1.0),vec3(1.0,.12,.3),sin(a*2.0+time*.4)*.5+.5);
          color*=ring*1.7+storm*.8;
          color+=vec3(1.0,.65,.8)*ring*power*.7;
          float alpha=max(1.0-smoothstep(.27,.3,r),ring+storm*.7);
          gl_FragColor=vec4(color,clamp(alpha,0.0,1.0));
          #include <colorspace_fragment>
        }`,
        toneMapped: false,
      })
    );
    horizon.name = 'core-event-horizon';
    horizon.position.set(0, 0.65, -149);
    group.add(horizon);
  }
  return {
    group,
    profile,
    particles,
    membrane,
    update(time, progress, cameraZ, dieZ) {
      group.visible = time > 0.7;
      membrane.uniforms.time.value = time;
      membrane.uniforms.power.value = progress;
      const spin = profile.twist ? time * profile.twist * 0.2 : 0;
      ribbons.rotation.z = spin;
      rings.forEach((ring, i) => {
        ring.rotation.z = i * profile.twist * 2 + spin * (i % 2 ? -1 : 1);
        const pulse = 1 + Math.sin(time * 1.8 - i * 0.5) * 0.025;
        ring.scale.set(pulse, pulse, 1);
      });
      for (let i = 0; i < tokens.count; i++) {
        const angle = i * 2.39996 + time * profile.twist;
        const radius = 2.5 + (i % 7) * 0.16;
        const outline = getWarpOutline(profile, angle);
        dummy.position.set(
          outline.x * radius,
          outline.y * radius + 0.65,
          cameraZ +
            8 -
            (((i / tokens.count) * length + time * (4 + progress * 12)) %
              length)
        );
        dummy.rotation.set(time * 0.4 + i, i * 0.7 + time * 0.3, i * 0.2);
        dummy.scale.setScalar(0.5 + (i % 4) * 0.2);
        if (profile.token === 'prism' || profile.token === 'shard')
          dummy.scale.y *= 1.8;
        dummy.updateMatrix();
        tokens.setMatrixAt(i, dummy.matrix);
      }
      tokens.instanceMatrix.needsUpdate = true;
      for (let i = 0; i < streakCount; i++) {
        const angle = i * 2.39996;
        const radius = 1.7 + (i % 13) * 0.16;
        const z =
          cameraZ +
          10 -
          (((i / streakCount) * 100 + time * (24 + progress * 50)) % 100);
        const x = Math.cos(angle) * radius,
          y = Math.sin(angle) * radius + 0.65;
        streakPositions.set(
          [x, y, z, x, y, z - (0.4 + progress * 4 + (i % 3) * 0.3)],
          i * 6
        );
      }
      streakGeometry.attributes.position.needsUpdate = true;
      particles.material.opacity = smooth(progress, 0, 0.2) * 0.65;
      gates.forEach(({ gate, blades, z }, index) => {
        const opening = smooth(16 - (dieZ - z), 0, 14);
        gate.rotation.z = index * 0.28 + opening * 0.25;
        blades.forEach((blade, i) => {
          const angle = (i / blades.length) * TAU + Math.PI / 2;
          blade.position.set(
            Math.cos(angle) * opening * 3.7,
            Math.sin(angle) * opening * 3.7,
            0
          );
          blade.rotation.z = (i / blades.length) * TAU + opening * 0.38;
        });
      });
      if (horizon) {
        horizon.material.uniforms.time.value = time;
        horizon.material.uniforms.power.value = smooth(progress, 0.55, 1);
        horizon.rotation.z = -time * 0.04;
      }
    },
  };
};
